import moment from "moment"
import _ from 'lodash'

class MonthOfYear {
    month: number
    year: number

    static regex = /([0-9]{4})-([0-9]{2})/

    constructor(month: number | string, year: number | string) {
        const m = parseInt(String(month))
        if (!_.isInteger(m) || (m < 1 || m > 12)) {
            throw new Error(`Month should be in 1..12 range, ${m}`)
        }
        const y = parseInt(String(year))
        if (!_.isInteger(y) || (y < 1000 || y > 9999)) {
            throw new Error("Year should be in 1000..9999 range")
        }
        this.month = m
        this.year = y
    }

    firstDay(): moment.Moment {
        return moment(`${this.year}-${this.month}-01`, "YYYY-MM-DD")
    }

    lastDay(): moment.Moment {
        return this.firstDay().endOf("month")
    }

    nextMonth(): MonthOfYear {
        const endOfYear = this.month === 12
        return new MonthOfYear(endOfYear ? 1 : this.month + 1, endOfYear ? this.year + 1 : this.year)
    }

    previousMonth(): MonthOfYear {
        const startOfYear = this.month === 1
        return new MonthOfYear(startOfYear ? 12 : this.month - 1, startOfYear ? this.year - 1 : this.year)
    }

    previousYear(): MonthOfYear {
        return new MonthOfYear(this.month, this.year - 1)
    }

    nextYear(): MonthOfYear {
        return new MonthOfYear(this.month, this.year + 1)
    }

    static now(): MonthOfYear {
        return this.fromDate(new Date())
    }

    static fromMoment(m: moment.Moment): MonthOfYear {
        return new MonthOfYear(m.month() + 1, m.year())
    }

    static fromDate(date: Date): MonthOfYear {
        return new MonthOfYear(date.getMonth() + 1, date.getFullYear())
    }

    static parse(str: string): MonthOfYear | null {
        const regex = /([0-9]{4})-([0-9]{2})/gm
        const match = regex.exec(str)
        if (!match) {
            return null
        }

        const year = parseInt(match[1])
        const month = parseInt(match[2])
        if (month < 1 || month > 12) {
            return null
        }
        if (year < 1000 || year > 9999) {
            return null
        }

        return new MonthOfYear(month, year)
    }

    toString(): string {
        return `${this.year.toString().padStart(4, '0')}-${this.month.toString().padStart(2, '0')}`
    }

    toMonthYearString(): string {
        return `${this.month.toString().padStart(2, '0')}-${this.year.toString().padStart(4, '0')}`
    }

    min(other: MonthOfYear): MonthOfYear {
        return this.compare(other) > 0 ? other : this
    }

    max(other: MonthOfYear): MonthOfYear {
        return this.compare(other) > 0 ? this : other
    }

    compare(other: MonthOfYear): number {
        if (this.year === other.year) {
            if (this.month === other.month) {
                return 0
            }
            return this.month > other.month ? 1 : -1
        } else {
            return this.year > other.year ? 1 : -1
        }
    }
}

export default MonthOfYear
